import React from 'react';

const ModelMetricsTable = ({ metrics = {}, theme = 'light' }) => {
  const isEmpty = !metrics || (typeof metrics === 'object' && Object.keys(metrics).length === 0);

  const fmtNum = (v, digits = 2) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '-';
    return n.toFixed(digits);
  };

  const fmtPct = (v, digits = 2) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '-';
    return `${n.toFixed(digits)}%`;
  };

  const fmtDateCET = (value) => {
    try {
      let d = null;
      if (!value) return '-';
      if (typeof value === 'string' || typeof value === 'number') {
        d = new Date(value);
      } else if (value && typeof value === 'object' && ('$date' in value)) {
        d = new Date(value.$date);
      }
      if (!d || isNaN(d.getTime())) return '-';
      const dateStr = new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', timeZone: 'Europe/Berlin' }).format(d);
      const timeStr = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Berlin' }).format(d);
      return `${dateStr}, ${timeStr}`;
    } catch {
      return '-';
    }
  };

  const extractModelBase = (s) => {
    if (!s || typeof s !== 'string') return '';
    // Prefer stripping trailing _{SYMBOL}_evaluate if present, preserving internal model underscores
    const m = s.match(/(.+)_([A-Za-z0-9.-]+)_evaluate$/);
    if (m) return m[1];
    // Fallback: if there are underscores, take the first segment as a conservative base
    if (s.includes('_')) return s.split('_')[0];
    return s;
  };

  const rows = Object.entries(metrics || {}).map(([key, m]) => {
    const trainingDate = m?.training_date ?? m?.trained_at ?? null;
    const sortable = (() => {
      try {
        if (!trainingDate) return 0;
        if (typeof trainingDate === 'string' || typeof trainingDate === 'number') return new Date(trainingDate).getTime();
        if (trainingDate && typeof trainingDate === 'object' && ('$date' in trainingDate)) return new Date(trainingDate.$date).getTime();
      } catch {}
      return 0;
    })();
    const rawName = m?.model_name || key || '';
    const displayName = extractModelBase(rawName);
    return { key, ...m, _trainingDateSortable: sortable, _displayName: displayName };
  })
  // Sort by model name (alphabetical). Use display name, fall back to raw name/key; tie-breaker: training date desc.
  .sort((a, b) => {
    const aName = (a?._displayName || a?.model_name || a?.key || '').toString().toLowerCase();
    const bName = (b?._displayName || b?.model_name || b?.key || '').toString().toLowerCase();
    if (aName < bName) return -1;
    if (aName > bName) return 1;
    return (b._trainingDateSortable || 0) - (a._trainingDateSortable || 0);
  });

  if (isEmpty) {
    return (
      <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
        No model metrics available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className={`min-w-full text-xs md:text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
        <thead>
          <tr className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <th className="px-3 py-2 text-center font-semibold">Model</th>
            <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">Training Date (CET)</th>
            <th className="px-3 py-2 text-center font-semibold">MAPE</th>
            <th className="px-3 py-2 text-center font-semibold">MAE</th>
            <th className="px-3 py-2 text-center font-semibold">RMSE</th>
            <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">Next-day Direction Acc.</th>
            <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">Forward Trend Acc.</th>
            <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">Test Loss</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, idx) => (
            <tr key={m?.run_id || m?.model_name || m.key || idx} className={idx % 2 === 0 ? (theme === 'dark' ? 'bg-gray-800' : 'bg-white') : (theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50')}>
              <td className="px-3 py-2 align-top">
                <div className="font-medium">{m?._displayName || '-'}</div>
              </td>
              <td className="px-3 py-2 align-top text-center whitespace-nowrap">{fmtDateCET(m?.training_date)}</td>
              <td className="px-3 py-2 align-top text-center">{fmtPct(m?.MAPE)}</td>
              <td className="px-3 py-2 align-top text-center">{fmtNum(m?.MAE)}</td>
              <td className="px-3 py-2 align-top text-center">{fmtNum(m?.RMSE)}</td>
              <td className="px-3 py-2 align-top text-center">{fmtPct(m?.next_day_direction_accuracy)}</td>
              <td className="px-3 py-2 align-top text-center">{fmtPct(m?.forward_trend_accuracy)}</td>
              <td className="px-3 py-2 align-top text-center">{fmtNum(m?.test_loss, 6)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ModelMetricsTable;
